import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'error',
    'manualOption',
    'paymentMethod',
    'paymentMethodsError',
    'savePaymentMethod',
    'savedOption',
    'source',
    'submit'
  ]

  static values = {
    manualPaymentUrl: String,
    paymentMethodsUrl: String,
    savedPaymentUrl: String,
    serviceRequestId: Number,
    token: String
  }

  connect () {
    this.paymentMethods = []
    this.submitting = false

    this.resetSelection()
    this.loadPaymentMethods()
    this.updateState()
  }

  resetSelection () {
    for (const source of this.sourceTargets) {
      source.checked = false
    }

    this.paymentMethodTarget.value = ''
    this.savePaymentMethodTarget.checked = false
  }

  async loadPaymentMethods () {
    try {
      const url = new URL(
        this.paymentMethodsUrlValue,
        window.location.origin
      )

      if (this.hasTokenValue && this.tokenValue) {
        url.searchParams.set('token', this.tokenValue)
      }

      const response = await fetch(url, {
        headers: {
          Accept: 'application/json'
        },
        credentials: 'same-origin'
      })

      if (!response.ok) {
        throw new Error(`Payment methods request failed: ${response.status}`)
      }

      const body = await response.json()

      const paymentMethods = body.payment_methods || []

      this.paymentMethods = [...paymentMethods].sort((left, right) => {
        const leftDate = left.used_at ? new Date(left.used_at) : new Date(0)
        const rightDate = right.used_at ? new Date(right.used_at) : new Date(0)

        return rightDate - leftDate
      })

      this.renderPaymentMethods()
    } catch (error) {
      console.error(error)

      this.paymentMethods = []
      this.renderPaymentMethods()
      this.paymentMethodsErrorTarget.classList.remove('hidden')
    }
  }

  renderPaymentMethods () {
    this.paymentMethodTarget.replaceChildren()

    const prompt = document.createElement('option')
    prompt.value = ''

    if (this.paymentMethods.length === 0) {
      prompt.textContent = 'No saved payment methods available'
    } else {
      prompt.textContent = 'Select a saved payment method'
    }

    this.paymentMethodTarget.append(prompt)

    for (const paymentMethod of this.paymentMethods) {
      const option = document.createElement('option')

      option.value = paymentMethod.id
      option.textContent = this.paymentMethodLabel(paymentMethod)

      this.paymentMethodTarget.append(option)
    }

    const defaultPaymentMethod = this.paymentMethods.find(
      paymentMethod => paymentMethod.default
    )

    if (defaultPaymentMethod) {
      this.paymentMethodTarget.value =
      defaultPaymentMethod.id.toString()
    }

    this.updateState()
  }

  paymentMethodLabel (paymentMethod) {
    if (paymentMethod.option_text) {
      return paymentMethod.option_text
    }

    const type = paymentMethod.account_type
      ?.replaceAll('_', ' ')
      .replace(/\b\w/g, character => character.toUpperCase())

    const accountNumber = paymentMethod.masked_account_number || ''
    const ending = accountNumber.slice(-4)

    return [type, ending && `ending in ${ending}`]
      .filter(Boolean)
      .join(' ')
  }

  sourceChanged () {
    this.clearError()
    this.updateState()
  }

  paymentMethodChanged () {
    this.clearError()
    this.updateState()
  }

  selectSavedSource () {
    const savedSource = this.sourceTargets.find(
      source => source.value === 'saved'
    )

    if (!savedSource) {
      return
    }

    savedSource.checked = true

    this.clearError()
    this.updateState()
  }

  updateState () {
    const source = this.selectedSource

    const manualSelected = source === 'manual'
    const savedSelected = source === 'saved'
    const savedMethodsAvailable = this.paymentMethods.length > 0

    this.paymentMethodTarget.disabled =
      !savedMethodsAvailable || this.submitting

    this.savePaymentMethodTarget.disabled =
      !manualSelected || this.submitting

    if (!manualSelected) {
      this.savePaymentMethodTarget.checked = false
    }

    this.submitTarget.disabled =
      this.submitting ||
      source === null ||
      (savedSelected && !this.paymentMethodTarget.value)

    this.manualOptionTarget.classList.toggle(
      'ring-2',
      source === 'manual'
    )

    this.manualOptionTarget.classList.toggle(
      'ring-blue-500',
      source === 'manual'
    )

    this.savedOptionTarget.classList.toggle(
      'ring-2',
      source === 'saved'
    )

    this.savedOptionTarget.classList.toggle(
      'ring-blue-500',
      source === 'saved'
    )
  }

  async submit () {
    if (this.submitTarget.disabled) {
      return
    }

    this.clearError()
    this.setSubmitting(true)

    try {
      if (this.selectedSource === 'saved') {
        await this.submitSavedPayment()
      } else if (this.selectedSource === 'manual') {
        await this.submitManualPayment()
      }
    } catch (error) {
      console.error(error)

      this.showError(
        error.message ||
        'The payment could not be started. Please try again.'
      )

      this.setSubmitting(false)
    }
  }

  async submitSavedPayment () {
    const response = await fetch(this.savedPaymentUrlValue, {
      method: 'POST',
      headers: this.jsonHeaders,
      credentials: 'same-origin',
      body: JSON.stringify({
        payment: {
          service_request_id: this.serviceRequestIdValue,
          payment_method_id: this.paymentMethodTarget.value
        },
        token: this.tokenValue
      })
    })

    const body = await this.parseResponse(response)

    if (!response.ok) {
      throw new Error(
        body.error || 'The saved payment method could not be charged.'
      )
    }

    switch (body.AUTH_RESP) {
      case '00':
        document.open()
        document.write(body.html)
        document.close()
        return
      case '54':
        throw new Error(
          'The selected payment method has expired. Please choose another method or enter card information manually.'
        )
      default:
        throw new Error(
          body.AUTH_RESP_TEXT ||
          `Payment failed with response code ${body.AUTH_RESP}.`
        )
    }
  }

  async submitManualPayment () {
    const response = await fetch(this.manualPaymentUrlValue, {
      method: 'POST',
      headers: this.jsonHeaders,
      credentials: 'same-origin',
      body: JSON.stringify({
        tac: {
          invoice_number: this.serviceRequestIdValue,
          enable_save_payment_method: this.savePaymentMethodTarget.checked
        },
        token: this.tokenValue
      })
    })

    const body = await this.parseResponse(response)

    if (!response.ok || body.error) {
      throw new Error(
        body.error || 'The secure payment page could not be opened.'
      )
    }

    this.postForm(body.paypage_url, body.parameters)
  }

  postForm (path, parameters) {
    const form = document.createElement('form')

    form.method = 'post'
    form.action = path

    for (const [name, value] of Object.entries(parameters)) {
      const input = document.createElement('input')

      input.type = 'hidden'
      input.name = name
      input.value = value

      form.append(input)
    }

    document.body.append(form)
    form.submit()
  }

  async parseResponse (response) {
    try {
      return await response.json()
    } catch (_error) {
      return {}
    }
  }

  setSubmitting (submitting) {
    this.submitting = submitting
    this.submitTarget.textContent = submitting
      ? 'Processing…'
      : 'Pay Now'

    this.updateState()
  }

  showError (message) {
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove('hidden')
  }

  clearError () {
    this.errorTarget.textContent = ''
    this.errorTarget.classList.add('hidden')
  }

  get selectedSource () {
    return this.sourceTargets.find(source => source.checked)?.value || null
  }

  get jsonHeaders () {
    return {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-Token': document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute('content') || ''
    }
  }
}
